import { Button, FlexBox, Skeleton, Text } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { useCallback } from 'react';

import { ConversationAutomationAuditLogEntry } from 'apis/auditLogs';
import { queuesApi } from 'apis/queues';
import { useAppNavigate } from 'utils/url/useAppNavigate';

type RoutedThroughQueueProps = {
  node: ConversationAutomationAuditLogEntry<'AUTOMATION_QUEUE_NODE_VISITED'>;
};

export function RoutedThroughQueue({
  node,
}: RoutedThroughQueueProps): JSX.Element {
  const hasQueueAccess = useHasAccess('settings.queue:r');

  const navigate = useAppNavigate();

  const { isLoading, isFetching, isError, name } = queuesApi.useReadQueueQuery(
    {
      id: node.queue_id ?? '',
    },
    {
      skip: !!node.error,
      selectFromResult: ({ data, ...rest }) => {
        return {
          name: data?.data?.name ?? '',
          ...rest,
        };
      },
    },
  );

  const handleGoToQueue = useCallback(() => {
    navigate({
      path: '/settings/queues/:id',
      params: {
        path: {
          id: node.queue_id ?? '',
        },
      },
    });
  }, [node.queue_id, navigate]);

  if (node.error) {
    return (
      <Text.Paragraph>Queue · Failed with error: {node.error}</Text.Paragraph>
    );
  }

  if (isLoading || isFetching) {
    return <Skeleton height="18px" width="200px" />;
  }

  if (isError && hasQueueAccess) {
    return (
      <FlexBox margin="300 0">
        <Button variant="secondary" onClick={handleGoToQueue}>
          Go to queue
        </Button>
      </FlexBox>
    );
  }

  return (
    <Text.Paragraph>
      Queue · {`Conversation transferred to queue ${name}`}
    </Text.Paragraph>
  );
}
